import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { Modal } from "../";
import {
  useCreateExtraOption,
  useUpdateExtraOption,
} from "../../../hooks/useProducts";

const CreateOptionModal = ({
  isOpen,
  onClose,
  businessId,
  extraId,
  optionToEdit = null,
}) => {
  const toast = useToast();
  const createMutation = useCreateExtraOption();
  const updateMutation = useUpdateExtraOption();
  const isEditMode = !!optionToEdit;

  const [formData, setFormData] = useState({
    name: "",
    min: "1",
    max: "1",
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when optionToEdit changes
  useEffect(() => {
    if (optionToEdit) {
      setFormData({
        name: optionToEdit.name || "",
        min: optionToEdit.min?.toString() || "1",
        max: optionToEdit.max?.toString() || "1",
      });
    } else {
      setFormData({
        name: "",
        min: "1",
        max: "1",
      });
    }
    setErrors({});
  }, [optionToEdit, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // Solo permitir números enteros
    if (value === "" || /^\d+$/.test(value)) {
      // Limitar a 15 caracteres
      const limitedValue = value.slice(0, 15);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.min?.trim()) {
      newErrors.min = "Min is required";
    } else if (isNaN(parseInt(formData.min)) || parseInt(formData.min) < 0) {
      newErrors.min = "Min must be a valid positive integer";
    }
    if (!formData.max?.trim()) {
      newErrors.max = "Max is required";
    } else if (isNaN(parseInt(formData.max)) || parseInt(formData.max) < 0) {
      newErrors.max = "Max must be a valid positive integer";
    }
    // Validar que max no sea menor que min (solo si ambos son válidos)
    if (
      !newErrors.min &&
      !newErrors.max &&
      formData.min?.trim() &&
      formData.max?.trim()
    ) {
      const minValue = parseInt(formData.min);
      const maxValue = parseInt(formData.max);
      if (maxValue < minValue) {
        newErrors.max = "Max cannot be less than Min";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        name: formData.name?.trim(),
        min: parseInt(formData.min) || 1,
        max: parseInt(formData.max) || 1,
      };

      if (isEditMode) {
        await updateMutation.mutateAsync({
          businessId,
          extraId,
          optionId: optionToEdit.id,
          payload,
        });
        handleApiSuccess("Option updated successfully", toast);
      } else {
        await createMutation.mutateAsync({
          businessId,
          extraId,
          payload: {
            ...payload,
            conditioned: false,
            enabled: true,
          },
        });
        handleApiSuccess("Option created successfully", toast);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: isEditMode
          ? "Failed to Update Option"
          : "Failed to Create Option",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      min: "1",
      max: "1",
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Option" : "Create Option"}
      subtitle={
        isEditMode ? "Update option details" : "Add a new option to this extra"
      }
      maxWidth="max-w-md"
      className="ordering-catalog-surface"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-white">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Option name"
            required
          />
          {errors.name && (
            <div className="text-destructive mt-1 text-xs">{errors.name}</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-white">
              Min <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              name="min"
              value={formData.min}
              onChange={handleNumberChange}
              maxLength={15}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
              placeholder="1"
              required
            />
            {errors.min && (
              <div className="text-destructive mt-1 text-xs">{errors.min}</div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-white">
              Max <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              name="max"
              value={formData.max}
              onChange={handleNumberChange}
              maxLength={15}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
              placeholder="1"
              required
            />
            {errors.max && (
              <div className="text-destructive mt-1 text-xs">{errors.max}</div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.name.trim() ||
              !formData.min.trim() ||
              !formData.max.trim()
            }
            className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditMode
              ? updateMutation.isPending
                ? "Updating..."
                : "Update Option"
              : createMutation.isPending
                ? "Creating..."
                : "Create Option"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateOptionModal;
